class CustomTabs {
  constructor(section) {
    this.section = section;
    this.buttons = Array.from(section.querySelectorAll('.product-tabs__button'));
    this.panels = Array.from(section.querySelectorAll('.custom-tab'));
    this.headers = Array.from(section.querySelectorAll('.custom-tab__title'));
    this.navigation = section.querySelector('.product-tabs__navigation');
    this.content = section.querySelector('.product-tabs__content');
    this.activeIndex = 0;
    this.isClickScrolling = false;
    this.clickScrollTimeout = null;

    this.init();
  }

  init() {
    this.loadTabTitles();
    this.applyHeadingFontSize();
    this.hideEmptyTabs();
    this.bindEvents();
    this.setupObserver();
    this.setActiveTab(0, false);

    if (this.section instanceof HTMLElement) {
      this.section.setAttribute('data-init', 'done');
    }
  }

  loadTabTitles() {
    this.headers.forEach((header, index) => {
      const button = this.buttons[index];
      if (!header || !button) return;
      const title = header.textContent?.trim();
      if (!title) return;
      const textSpan = button.querySelector('.product-tabs__button-text');
      const skeletonSpan = button.querySelector('.product-tabs__button-skeleton');
      if (textSpan instanceof HTMLElement) {
        textSpan.textContent = title;
        textSpan.style.display = 'inline-block';
      }
      if (skeletonSpan instanceof HTMLElement) {
        skeletonSpan.style.display = 'none';
      }
    });
  }

  applyHeadingFontSize() {
    const sample = this.buttons.find((b) => !b.hasAttribute('data-hidden'));
    if (!sample) return;
    const textSpan = sample.querySelector('.product-tabs__button-text');
    if (!(textSpan instanceof HTMLElement)) return;
    const tabFontSize = parseFloat(window.getComputedStyle(textSpan).fontSize);
    if (!tabFontSize) return;
    const headingSize = `${tabFontSize + 3}px`;
    this.headers.forEach((header) => {
      if (header instanceof HTMLElement) {
        header.style.fontSize = headingSize;
      }
    });
  }

  isTabContentEmpty(panel) {
    if (!panel) return true;
    const children = Array.from(panel.children);
    const validChildren = children.filter(
      (child) => child.tagName.toLowerCase() !== 'style' && !child.classList.contains('custom-tab__title')
    );
    return validChildren.length < 2;
  }

  hideEmptyTabs() {
    this.panels.forEach((panel, index) => {
      const button = this.buttons[index];
      if (panel instanceof HTMLElement && this.isTabContentEmpty(panel)) {
        if (button instanceof HTMLElement) {
          button.style.display = 'none';
          button.setAttribute('data-hidden', 'true');
        }
        panel.style.display = 'none';
        panel.setAttribute('data-hidden', 'true');
      }
    });

    this.visibleButtons = this.buttons.filter((b) => !b.hasAttribute('data-hidden'));
    this.visiblePanels = this.panels.filter((p) => !p.hasAttribute('data-hidden'));

    if (this.navigation instanceof HTMLElement) {
      if (this.visiblePanels.length <= 1) {
        this.navigation.style.display = 'none';
        this.navigation.setAttribute('data-hidden', 'true');
      } else {
        this.navigation.removeAttribute('data-hidden');
      }
    }
  }

  bindEvents() {
    this.buttons.forEach((button, index) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (button.hasAttribute('data-hidden')) return;
        this.scrollToPanel(index);
      });
    });
  }

  getScrollOffset() {
    const headerVar = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
    const headerHeight = parseFloat(headerVar) || 0;
    const navHeight = this.navigation instanceof HTMLElement ? this.navigation.offsetHeight : 0;
    return headerHeight + navHeight + 8;
  }

  isScrollLocked() {
    // Modals (search, cart drawer, …) pin the page with `body { position: fixed; top: -Ypx }`,
    // which takes the body out of flow: documentElement.scrollHeight collapses to the viewport
    // and scrollY drops to 0. The scroll event that follows would otherwise look like
    // "user reached the bottom of the page" and activate the last tab.
    return document.body.style.position === 'fixed' || document.documentElement.hasAttribute('scroll-lock');
  }

  scrollToPanel(index) {
    const panel = this.panels[index];
    if (!(panel instanceof HTMLElement)) return;

    this.isClickScrolling = true;
    this.setActiveTab(index, true);

    panel.scrollIntoView({ block: 'start', behavior: 'smooth' });

    if (this.clickScrollTimeout) clearTimeout(this.clickScrollTimeout);
    this.clickScrollTimeout = setTimeout(() => {
      this.isClickScrolling = false;
    }, 800);
  }

  setupObserver() {
    const visiblePanels = /** @type {HTMLElement[]} */ (this.visiblePanels || []);
    if (visiblePanels.length <= 1) return;

    const updateActive = () => {
      if (this.isClickScrolling || this.isScrollLocked()) return;

      const offset = this.getScrollOffset();
      const scrollBottom = window.scrollY + window.innerHeight;
      const pageBottom = document.documentElement.scrollHeight;

      let activePanel = null;

      if (pageBottom - scrollBottom < 4) {
        activePanel = visiblePanels[visiblePanels.length - 1] || null;
      } else {
        const probeY = offset + 1;
        for (let i = 0; i < visiblePanels.length; i++) {
          const panel = visiblePanels[i];
          if (!panel) continue;
          const rect = panel.getBoundingClientRect();
          if (rect.top <= probeY && rect.bottom > probeY) {
            activePanel = panel;
            break;
          }
        }
        if (!activePanel) {
          for (let i = visiblePanels.length - 1; i >= 0; i--) {
            const panel = visiblePanels[i];
            if (!panel) continue;
            const rect = panel.getBoundingClientRect();
            if (rect.top <= probeY) {
              activePanel = panel;
              break;
            }
          }
        }
      }

      if (!activePanel) return;
      const idx = this.panels.indexOf(activePanel);
      if (idx >= 0 && idx !== this.activeIndex) {
        this.setActiveTab(idx, false);
      }
    };

    let ticking = false;
    this._scrollHandler = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateActive();
        ticking = false;
      });
    };

    window.addEventListener('scroll', this._scrollHandler, { passive: true });
    window.addEventListener('resize', this._scrollHandler, { passive: true });
    updateActive();
  }

  setActiveTab(index, scrollIntoView) {
    this.activeIndex = index;
    this.buttons.forEach((button, i) => {
      const isActive = i === index;
      const isHidden = button.hasAttribute('data-hidden');
      if (!isHidden) {
        button.classList.toggle('product-tabs__button--active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      }
    });

    if (scrollIntoView && this.navigation instanceof HTMLElement) {
      const activeBtn = this.buttons[index];
      if (activeBtn instanceof HTMLElement) {
        const inner = this.navigation.querySelector('.product-tabs__navigation-inner');
        if (inner instanceof HTMLElement) {
          const btnLeft = activeBtn.offsetLeft;
          const btnCenter = btnLeft - inner.clientWidth / 2 + activeBtn.clientWidth / 2;
          inner.scrollTo({ left: btnCenter, behavior: 'smooth' });
        }
      }
    }
  }
}

const initTabs = (section) => {
  if (section.querySelector('.product-tabs__navigation') && section instanceof HTMLElement) {
    new CustomTabs(section);
  }
};

const syncNavHeight = (root) => {
  const nav = root.querySelector('.product-tabs__navigation');
  if (!(nav instanceof HTMLElement)) return;
  const height = nav.offsetHeight;
  if (height) root.style.setProperty('--product-tabs-nav-height', `${height}px`);
};

const mirrorHeaderHeight = () => {
  const value = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
  if (value) document.documentElement.style.setProperty('--header-height', value);
};

const initHeaderHeightMirror = () => {
  mirrorHeaderHeight();
  const headerGroup = document.querySelector('#header-group');
  if (headerGroup instanceof HTMLElement && 'ResizeObserver' in window) {
    new ResizeObserver(mirrorHeaderHeight).observe(headerGroup);
  }
};

const initNavHeights = () => {
  document.querySelectorAll('.product-tabs').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    syncNavHeight(root);
    const nav = root.querySelector('.product-tabs__navigation');
    if (nav instanceof HTMLElement && 'ResizeObserver' in window) {
      new ResizeObserver(() => syncNavHeight(root)).observe(nav);
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-section-id]').forEach(initTabs);
  initNavHeights();
  initHeaderHeightMirror();
});

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement) {
    const section = target.querySelector('[data-section-id]');
    if (section) initTabs(section);
  }
});
